import React, { useState } from 'react';
import { supabase } from '../supabaseClient';

function Reports({ orgId, plan }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState(false);

  if (plan === 'free') {
    return (
      <div>
        <div className="header">
          <h2>📊 Reports</h2>
          <p>Attendance history and trends</p>
        </div>
        <div style={upgradeBoxStyle}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h3 style={{ marginBottom: '8px' }}>Reports are a Premium Feature</h3>
          <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
            Upgrade to a paid plan to unlock attendance reports, child history, and CSV exports.
          </p>
          <div style={featureListStyle}>
            <p>✅ Daily, weekly & monthly reports</p>
            <p>✅ Full child check-in history</p>
            <p>✅ Download as CSV spreadsheet</p>
            <p>✅ Peak hours analysis</p>
          </div>
          <button className="btn btn-primary" style={{ marginTop: '20px' }}>
            Upgrade to Paid Plan
          </button>
        </div>
      </div>
    );
  }

  const handleGenerateReport = async () => {
    setError('');
    setGenerated(false);

    if (!startDate || !endDate) {
      setError('Please select a start and end date.');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date must be before end date.');
      return;
    }

    setLoading(true);

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    const { data: checkins, error: checkinError } = await supabase
      .from('checkins')
      .select('*')
      .eq('organization_id', orgId)
      .gte('checked_in_at', start.toISOString())
      .lte('checked_in_at', end.toISOString())
      .order('checked_in_at', { ascending: false });

    if (checkinError) {
      setError('Error fetching report: ' + checkinError.message);
      setLoading(false);
      return;
    }

    if (!checkins.length) {
      setReportData([]);
      setSummary({ total: 0, checkedOut: 0, avgPerDay: 0 });
      setLoading(false);
      setGenerated(true);
      return;
    }

    const childIds = checkins.map((c) => c.child_id);
    const { data: children } = await supabase
      .from('children')
      .select('*')
      .in('id', childIds);

    const results = checkins.map((checkin) => {
      const child = children.find((c) => c.id === checkin.child_id);
      return {
        child_name: child?.full_name || 'Unknown',
        parent_name: child?.parent_name || 'Unknown',
        parent_phone: child?.parent_phone || 'Unknown',
        checked_in_at: checkin.checked_in_at,
        checked_out_at: checkin.checked_out_at,
        status: checkin.status,
      };
    });

    // Calculate summary
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    const checkedOut = results.filter((r) => r.status === 'checked_out').length;

    setSummary({
      total: results.length,
      checkedOut,
      avgPerDay: (results.length / days).toFixed(1),
    });

    setReportData(results);
    setLoading(false);
    setGenerated(true);
  };

  const handleDownloadCSV = () => {
    const headers = ['Child Name', 'Parent Name', 'Parent Phone', 'Checked In At', 'Checked Out At', 'Status'];
    const rows = reportData.map((r) => [
      r.child_name,
      r.parent_name,
      r.parent_phone,
      r.checked_in_at ? new Date(r.checked_in_at).toLocaleString() : '',
      r.checked_out_at ? new Date(r.checked_out_at).toLocaleString() : 'Still Checked In',
      r.status,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="header">
        <h2>📊 Reports</h2>
        <p>Attendance history and trends</p>
      </div>

      {/* Date Range Picker */}
      <div style={formBoxStyle}>
        <p className="section-title">Select Date Range</p>
        <label style={labelStyle}>Start Date</label>
        <input
          className="input"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <label style={labelStyle}>End Date</label>
        <input
          className="input"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <button className="btn btn-primary" onClick={handleGenerateReport} disabled={loading}>
          {loading ? 'Generating...' : '📊 Generate Report'}
        </button>
        {error && <div className="error-message">{error}</div>}
      </div>

      {/* Summary */}
      {generated && summary && (
        <div style={{ marginTop: '24px' }}>
          <p className="section-title">Summary</p>
          <div style={summaryGridStyle}>
            <div style={summaryBoxStyle}>
              <h3 style={{ margin: 0, fontSize: '28px' }}>{summary.total}</h3>
              <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>Total Check-Ins</p>
            </div>
            <div style={{ ...summaryBoxStyle, background: 'linear-gradient(135deg, #43e97b, #38f9d7)' }}>
              <h3 style={{ margin: 0, fontSize: '28px' }}>{summary.checkedOut}</h3>
              <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>Checked Out</p>
            </div>
            <div style={{ ...summaryBoxStyle, background: 'linear-gradient(135deg, #4facfe, #00f2fe)' }}>
              <h3 style={{ margin: 0, fontSize: '28px' }}>{summary.avgPerDay}</h3>
              <p style={{ margin: 0, fontSize: '13px', opacity: 0.9 }}>Avg Per Day</p>
            </div>
          </div>

          {/* Download Button */}
          {reportData.length > 0 && (
            <button
              className="btn btn-green"
              style={{ width: '100%', marginBottom: '16px' }}
              onClick={handleDownloadCSV}
            >
              ⬇️ Download CSV
            </button>
          )}

          {/* Report Table */}
          {reportData.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999' }}>No check-ins found for this date range.</p>
          )}

          {reportData.length > 0 && (
            <div>
              <p className="section-title">Detailed Records ({reportData.length})</p>
              {reportData.map((record, index) => (
                <div key={index} style={recordCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0' }}>👶 {record.child_name}</h3>
                      <p style={{ margin: '0 0 2px 0', color: '#666', fontSize: '14px' }}>Parent: {record.parent_name}</p>
                      <p style={{ margin: '0 0 2px 0', color: '#666', fontSize: '14px' }}>
                        In: {new Date(record.checked_in_at).toLocaleString()}
                      </p>
                      <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                        Out: {record.checked_out_at ? new Date(record.checked_out_at).toLocaleString() : 'Still Checked In'}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: record.status === 'checked_out' ? '#e8f8f0' : '#fff3cd',
                      color: record.status === 'checked_out' ? '#2e7d52' : '#856404',
                    }}>
                      {record.status === 'checked_out' ? 'Checked Out' : 'Checked In'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const formBoxStyle = {
  background: '#f8f9ff',
  borderRadius: '16px',
  padding: '20px',
};

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '600',
  color: '#555',
  marginBottom: '6px',
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '12px',
  marginBottom: '16px',
};

const summaryBoxStyle = {
  background: 'linear-gradient(135deg, #667eea, #764ba2)',
  color: 'white',
  borderRadius: '12px',
  padding: '16px 10px',
  textAlign: 'center',
};

const recordCardStyle = {
  background: '#f8f9ff',
  border: '1px solid #eee',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '10px',
};

const upgradeBoxStyle = {
  background: '#f8f9ff',
  border: '2px dashed #667eea',
  borderRadius: '16px',
  padding: '30px 20px',
  textAlign: 'center',
};

const featureListStyle = {
  background: 'white',
  borderRadius: '12px',
  padding: '16px',
  textAlign: 'left',
};

export default Reports;